import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit, IconFinance, IconHistory, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { primaryRoleLabel } from '../utils/roles';

// TS port of the source (Falah) app's pages/DonorDetail.jsx — renamed to
// Member. MemberController::show() loads `user, users.roles` and flattens
// the linked user's identity fields onto the top-level JSON via present().
interface MemberLinkedUser {
  id: number;
  full_name: string;
  email: string;
  mobile: string | null;
  roles: string[];
}

interface MemberEmployment {
  id: number;
  start_date: string;
  end_date: string | null;
  employer: { id: number; name: string } | null;
}

interface MemberRecord {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  mobile: string | null;
  address: string | null;
  gender: string | null;
  date_of_birth: string | null;
  member_since: string | null;
  member_type: string | null;
  notes: string | null;
  photo_url: string | null;
  bank_name: string | null;
  bank_branch: string | null;
  bank_account_name: string | null;
  bank_account_number: string | null;
  payout_method: 'bank_transfer' | 'cheque' | null;
  users?: MemberLinkedUser[];
  employments: MemberEmployment[];
  checklist: MemberChecklistEntry[];
  documents: MemberDocumentRecord[];
}

interface MemberChecklistEntry {
  id: number;
  item_name: string;
  is_done: boolean;
  completed_at: string | null;
  completed_by_name: string | null;
}

interface MemberDocumentRecord {
  id: number;
  category: string;
  original_name: string;
}

interface EmployerOption {
  id: number;
  name: string;
}

interface Beneficiary {
  id: number;
  full_name: string;
  relationship: string;
  date_of_birth: string | null;
  national_id_number: string | null;
  contact_phone: string | null;
  contact_address: string | null;
  share_percentage: string;
  is_primary: boolean;
  notes: string | null;
}

const emptyPayoutForm = {
  bank_name: '', bank_branch: '', bank_account_name: '', bank_account_number: '', payout_method: '',
};

const emptyBeneficiaryForm = {
  full_name: '', relationship: '', date_of_birth: '', national_id_number: '',
  contact_phone: '', contact_address: '', share_percentage: '', is_primary: false, notes: '',
};

export function MemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const { can } = useAuth();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [member, setMember] = useState<MemberRecord | null>(null);
  const [employers, setEmployers] = useState<EmployerOption[]>([]);
  const [assignment, setAssignment] = useState({ employer_id: '', start_date: '' });
  const [assignmentErrors, setAssignmentErrors] = useState<Record<string, string[]>>({});

  const [uploadCategory, setUploadCategory] = useState('');
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([]);
  const [editingPayout, setEditingPayout] = useState(false);
  const [payoutForm, setPayoutForm] = useState(emptyPayoutForm);
  const [payoutErrors, setPayoutErrors] = useState<Record<string, string[]>>({});

  const [addingBeneficiary, setAddingBeneficiary] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState<Beneficiary | null>(null);
  const [beneficiaryForm, setBeneficiaryForm] = useState(emptyBeneficiaryForm);
  const [beneficiaryErrors, setBeneficiaryErrors] = useState<Record<string, string[]>>({});

  const canUpdate = can('memberships.update');
  const canArchive = can('memberships.archive');
  const canManageContributions = can('memberships.contributions.manage');

  const load = () => api.get<MemberRecord>(`/members/${id}`).then((res) => setMember(res.data));
  const loadBeneficiaries = () =>
    api.get<Beneficiary[]>(`/members/${id}/beneficiaries`).then((res) => setBeneficiaries(res.data));

  const toggleChecklistItem = async (entry: MemberChecklistEntry) => {
    try {
      const res = await api.put<MemberRecord>(`/member-checklist-entries/${entry.id}`, { is_done: !entry.is_done });
      setMember(res.data);
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Save failed.');
    }
  };

  const uploadDocument = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fileInput = form.elements.namedItem('file') as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file || !uploadCategory) return;

    const body = new FormData();
    body.append('category', uploadCategory);
    body.append('file', file);
    try {
      await api.post(`/members/${id}/documents`, body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadCategory('');
      form.reset();
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const removeDocument = async (doc: MemberDocumentRecord) => {
    if (!(await confirmDelete(`Delete "${doc.original_name}"?`))) return;
    try {
      await api.delete(`/member-documents/${doc.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  useEffect(() => {
    load();
    loadBeneficiaries();
    api.get<{ data: EmployerOption[] }>('/employers', { params: { per_page: 100 } }).then((res) => setEmployers(res.data.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const totalShare = beneficiaries.reduce((sum, b) => sum + Number(b.share_percentage), 0);

  const openEditPayout = () => {
    if (!member) return;
    setPayoutForm({
      bank_name: member.bank_name ?? '', bank_branch: member.bank_branch ?? '',
      bank_account_name: member.bank_account_name ?? '', bank_account_number: member.bank_account_number ?? '',
      payout_method: member.payout_method ?? '',
    });
    setPayoutErrors({});
    setEditingPayout(true);
  };

  const submitPayout = async (e: FormEvent) => {
    e.preventDefault();
    if (!member) return;
    try {
      await api.put(`/members/${id}`, {
        first_name: member.first_name, last_name: member.last_name, email: member.email,
        mobile: member.mobile, address: member.address, gender: member.gender,
        date_of_birth: member.date_of_birth, member_since: member.member_since, member_type: member.member_type,
        notes: member.notes, ...payoutForm,
      });
      setEditingPayout(false);
      load();
    } catch (err) {
      setPayoutErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openAddBeneficiary = () => {
    setBeneficiaryForm(emptyBeneficiaryForm);
    setBeneficiaryErrors({});
    setAddingBeneficiary(true);
  };

  const openEditBeneficiary = (b: Beneficiary) => {
    setBeneficiaryForm({
      full_name: b.full_name, relationship: b.relationship, date_of_birth: b.date_of_birth ?? '',
      national_id_number: b.national_id_number ?? '', contact_phone: b.contact_phone ?? '',
      contact_address: b.contact_address ?? '', share_percentage: b.share_percentage, is_primary: b.is_primary,
      notes: b.notes ?? '',
    });
    setBeneficiaryErrors({});
    setEditingBeneficiary(b);
  };

  const submitBeneficiary = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editingBeneficiary) {
        await api.put(`/beneficiaries/${editingBeneficiary.id}`, beneficiaryForm);
        setEditingBeneficiary(null);
      } else {
        await api.post(`/members/${id}/beneficiaries`, beneficiaryForm);
        setAddingBeneficiary(false);
      }
      loadBeneficiaries();
    } catch (err) {
      setBeneficiaryErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeBeneficiary = async (b: Beneficiary) => {
    if (!(await confirmDelete(`Remove beneficiary ${b.full_name}?`))) return;
    await api.delete(`/beneficiaries/${b.id}`);
    loadBeneficiaries();
  };

  const assignEmployer = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/members/${id}/employments`, assignment);
      setAssignment({ employer_id: '', start_date: '' });
      setAssignmentErrors({});
      load();
    } catch (err) {
      setAssignmentErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async () => {
    if (!member) return;
    if (!(await confirmDelete(`Delete ${member.first_name} ${member.last_name}?`))) return;
    try {
      await api.delete(`/members/${id}`);
      navigate('/memberships');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!member) return null;

  const users = member.users ?? [];

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships')} />
          <IconButton icon={IconHistory} label="Statements" to={`/memberships/${id}/statements`} />
          {canManageContributions && (
            <IconButton icon={IconFinance} label="Contributions" to={`/memberships/${id}/contributions`} />
          )}
          {canUpdate && <IconButton icon={IconEdit} label="Edit" to={`/memberships/${id}/edit`} />}
          {canArchive && <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />}
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {member.photo_url ? (
              <img
                src={member.photo_url}
                alt=""
                style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
            ) : (
              <div className="avatar" style={{ width: 56, height: 56, fontSize: '1.25rem' }}>
                {member.first_name?.charAt(0) ?? '?'}
              </div>
            )}
            <div>
              <div className="detail-summary-title">
                {member.first_name} {member.last_name}
              </div>
              <div className="detail-summary-service">
                {labelFor(lists, 'member_type', member.member_type ?? '')}
              </div>
            </div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Email</span>
            <span className={`detail-item-value${member.email ? '' : ' empty'}`}>{member.email || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Mobile</span>
            <span className={`detail-item-value${member.mobile ? '' : ' empty'}`}>{member.mobile || 'Not set'}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Address</span>
            <span className={`detail-item-value${member.address ? '' : ' empty'}`}>
              {member.address || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Gender</span>
            <span className={`detail-item-value${member.gender ? '' : ' empty'}`}>
              {member.gender ? member.gender.charAt(0).toUpperCase() + member.gender.slice(1) : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Date of birth</span>
            <span className={`detail-item-value${member.date_of_birth ? '' : ' empty'}`}>
              {member.date_of_birth || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Member since</span>
            <span className={`detail-item-value${member.member_since ? '' : ' empty'}`}>
              {member.member_since || 'Not set'}
            </span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Notes</span>
          {member.notes ? (
            <p className="detail-notes">{member.notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>

      <div className="card">
        <h2>Checklist</h2>
        {member.checklist.length > 0 ? (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {member.checklist.map((entry) => (
              <li key={entry.id}>
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={entry.is_done}
                    disabled={!canUpdate}
                    onChange={() => toggleChecklistItem(entry)}
                  />
                  {entry.item_name}
                </label>
                {entry.is_done && (
                  <span className="muted">
                    {' '}
                    — {entry.completed_at} by {entry.completed_by_name}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No checklist items configured. Add some under System &gt; Member Checklist Items.</p>
        )}
      </div>

      <div className="card">
        <h2>Documents</h2>
        {canUpdate && (
          <form onSubmit={uploadDocument} className="form-grid">
            <label>
              Category *
              <select required value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
                <option value="">Select…</option>
                {listOptions(lists, 'member_document_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="span-2">
              File *
              <input type="file" name="file" required accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
            </label>
            <div className="form-actions">
              <button className="btn btn-primary">Upload</button>
            </div>
          </form>
        )}
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Category</th>
                <th>File</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {member.documents.length > 0 ? (
                member.documents.map((doc) => (
                  <tr key={doc.id}>
                    <td>{labelFor(lists, 'member_document_type', doc.category)}</td>
                    <td>{doc.original_name}</td>
                    <td className="right">
                      <IconButton
                        icon={IconDownload}
                        label="Download"
                        onClick={() => downloadFile(api, `/member-documents/${doc.id}/download`, doc.original_name)}
                      />
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => removeDocument(doc)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="muted center">
                    No documents uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Linked user</h2>
        <p className="muted">Portal account that signs in as this member.</p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Role</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.full_name}</td>
                  <td className="muted">{u.email}</td>
                  <td className="muted">{u.mobile}</td>
                  <td className="muted">{primaryRoleLabel(u.roles)}</td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No users linked to this member.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>Employment History</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employer</th>
                <th>Start</th>
                <th>End</th>
              </tr>
            </thead>
            <tbody>
              {member.employments.map((row) => (
                <tr key={row.id}>
                  <td>{row.employer?.name ?? '—'}</td>
                  <td className="muted">{row.start_date}</td>
                  <td className="muted">{row.end_date ?? 'Current'}</td>
                </tr>
              ))}
              {member.employments.length === 0 && (
                <tr>
                  <td colSpan={3} className="muted center">
                    No employers assigned yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {canUpdate && (
          <form onSubmit={assignEmployer} className="form-grid" style={{ marginTop: 16 }}>
            {assignmentErrors.general && <div className="alert alert-error span-2">{assignmentErrors.general[0]}</div>}
            <label>
              Employer *
              <select
                value={assignment.employer_id}
                onChange={(e) => setAssignment({ ...assignment, employer_id: e.target.value })}
                required
              >
                <option value="">—</option>
                {employers.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
              {assignmentErrors.employer_id && <small className="error">{assignmentErrors.employer_id[0]}</small>}
            </label>
            <label>
              Start date *
              <input
                type="date"
                value={assignment.start_date}
                onChange={(e) => setAssignment({ ...assignment, start_date: e.target.value })}
                required
              />
              {assignmentErrors.start_date && <small className="error">{assignmentErrors.start_date[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button className="btn btn-primary">Assign employer</button>
            </div>
          </form>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Bank &amp; Payout Details</h2>
          {canUpdate && (
            <IconButton icon={IconEdit} label="Edit" onClick={openEditPayout} />
          )}
        </div>
        <p className="muted">Where benefit claim payments are sent — required before a claim can be submitted.</p>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Payout Method</span>
            <span className={`detail-item-value${member.payout_method ? '' : ' empty'}`}>
              {member.payout_method === 'cheque' ? 'Cheque' : member.payout_method === 'bank_transfer' ? 'Bank Transfer' : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Bank</span>
            <span className={`detail-item-value${member.bank_name ? '' : ' empty'}`}>
              {member.bank_name ? `${member.bank_name} — ${member.bank_branch || 'Not set'}` : 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Account Name</span>
            <span className={`detail-item-value${member.bank_account_name ? '' : ' empty'}`}>
              {member.bank_account_name || 'Not set'}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Account Number</span>
            <span className={`detail-item-value${member.bank_account_number ? '' : ' empty'}`}>
              {member.bank_account_number || 'Not set'}
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Beneficiaries</h2>
          {canUpdate && (
            <button type="button" className="btn btn-ghost" onClick={openAddBeneficiary}>
              + Add
            </button>
          )}
        </div>
        <p className="muted">
          For death benefit claims. Total share:{' '}
          <strong style={{ color: totalShare !== 100 && beneficiaries.length > 0 ? '#c0392b' : undefined }}>
            {totalShare}%
          </strong>
          {totalShare !== 100 && beneficiaries.length > 0 && ' — shares should sum to 100%'}
        </p>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Relationship</th>
                <th>Share</th>
                <th>Primary</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {beneficiaries.map((b) => (
                <tr key={b.id}>
                  <td>{b.full_name}</td>
                  <td className="muted">{labelFor(lists, 'beneficiary_relationship', b.relationship)}</td>
                  <td>{b.share_percentage}%</td>
                  <td className="muted">{b.is_primary ? 'Yes' : ''}</td>
                  <td className="right">
                    {canUpdate && (
                      <>
                        <IconButton icon={IconEdit} label="Edit" onClick={() => openEditBeneficiary(b)} />
                        <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeBeneficiary(b)} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {beneficiaries.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No beneficiaries added yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingPayout && (
        <Modal title="Edit bank & payout details" onClose={() => setEditingPayout(false)}>
          <form onSubmit={submitPayout} className="form-grid">
            {payoutErrors.general && <div className="alert alert-error span-2">{payoutErrors.general[0]}</div>}
            <label className="span-2">
              Payout method
              <select
                value={payoutForm.payout_method}
                onChange={(e) => setPayoutForm({ ...payoutForm, payout_method: e.target.value })}
              >
                <option value="">Not set</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </select>
              {payoutErrors.payout_method && <small className="error">{payoutErrors.payout_method[0]}</small>}
            </label>
            <label>
              Bank name
              <input value={payoutForm.bank_name} onChange={(e) => setPayoutForm({ ...payoutForm, bank_name: e.target.value })} />
            </label>
            <label>
              Bank branch
              <input value={payoutForm.bank_branch} onChange={(e) => setPayoutForm({ ...payoutForm, bank_branch: e.target.value })} />
            </label>
            <label>
              Account name
              <input
                value={payoutForm.bank_account_name}
                onChange={(e) => setPayoutForm({ ...payoutForm, bank_account_name: e.target.value })}
              />
            </label>
            <label>
              Account number
              <input
                value={payoutForm.bank_account_number}
                onChange={(e) => setPayoutForm({ ...payoutForm, bank_account_number: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditingPayout(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {(addingBeneficiary || editingBeneficiary) && (
        <Modal
          title={editingBeneficiary ? 'Edit beneficiary' : 'Add beneficiary'}
          onClose={() => (editingBeneficiary ? setEditingBeneficiary(null) : setAddingBeneficiary(false))}
        >
          <form onSubmit={submitBeneficiary} className="form-grid">
            {beneficiaryErrors.general && <div className="alert alert-error span-2">{beneficiaryErrors.general[0]}</div>}
            <label className="span-2">
              Full name *
              <input
                value={beneficiaryForm.full_name}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, full_name: e.target.value })}
                required
              />
              {beneficiaryErrors.full_name && <small className="error">{beneficiaryErrors.full_name[0]}</small>}
            </label>
            <label>
              Relationship *
              <select
                value={beneficiaryForm.relationship}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, relationship: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {listOptions(lists, 'beneficiary_relationship').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {beneficiaryErrors.relationship && <small className="error">{beneficiaryErrors.relationship[0]}</small>}
            </label>
            <label>
              Share (%) *
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="100"
                value={beneficiaryForm.share_percentage}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, share_percentage: e.target.value })}
                required
              />
              {beneficiaryErrors.share_percentage && <small className="error">{beneficiaryErrors.share_percentage[0]}</small>}
            </label>
            <label>
              Date of birth
              <input
                type="date"
                value={beneficiaryForm.date_of_birth}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, date_of_birth: e.target.value })}
              />
            </label>
            <label>
              National ID number
              <input
                value={beneficiaryForm.national_id_number}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, national_id_number: e.target.value })}
              />
            </label>
            <label>
              Contact phone
              <input
                value={beneficiaryForm.contact_phone}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, contact_phone: e.target.value })}
              />
            </label>
            <label className="span-2">
              Contact address
              <input
                value={beneficiaryForm.contact_address}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, contact_address: e.target.value })}
              />
            </label>
            <label className="span-2 checkbox-label">
              <input
                type="checkbox"
                checked={beneficiaryForm.is_primary}
                onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, is_primary: e.target.checked })}
              />
              Primary beneficiary
            </label>
            <div className="form-actions span-2">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => (editingBeneficiary ? setEditingBeneficiary(null) : setAddingBeneficiary(false))}
              >
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
